import React, { useEffect, useMemo, useState } from "react";
import { FluentProvider, type Theme } from "@fluentui/react-components";
import { ChatPanel } from "./components/ChatPanel";
import { SettingsPanel } from "./components/SettingsPanel";
import { bnpDarkTheme, bnpLightTheme } from "./theme";
import { loadPolicy, type OrgPolicy } from "./policy";
import { DEFAULT_APP_NAME } from "./config";
import { loadSettings, resolveSettings, type AddinSettings } from "./settings";
import { initTelemetry } from "./telemetry";

const DARK_MODE_MEDIA_QUERY = "(prefers-color-scheme: dark)";

/**
 * Resolves the active Fluent UI theme (Axis 12: dark mode) from the user's
 * stored preference and the current OS-level dark-mode signal. Kept as a
 * pure function — separate from the `matchMedia`/DOM wiring in `App` — so
 * the preference→theme mapping is unit testable without mocking the DOM.
 */
export function resolveTheme(
  preference: AddinSettings["themePreference"],
  systemPrefersDark: boolean
): Theme {
  if (preference === "dark") {
    return bnpDarkTheme;
  }
  if (preference === "light") {
    return bnpLightTheme;
  }
  return systemPrefersDark ? bnpDarkTheme : bnpLightTheme;
}

function getSystemPrefersDark(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(DARK_MODE_MEDIA_QUERY).matches
    : false;
}

export function App() {
  const [showSettings, setShowSettings] = useState(false);
  const [appName, setAppName] = useState(DEFAULT_APP_NAME);
  // Loaded once and threaded down to ChatPanel/RulesPanel so every live AI
  // call resolves effective settings via `resolveSettings(policy)` — an
  // imposed/locked org value must win over a stored user override
  // regardless of whether Settings was ever opened. See CLAUDE.md B6/B7.
  const [policy, setPolicy] = useState<OrgPolicy>({});
  const [themePreference, setThemePreference] = useState<AddinSettings["themePreference"]>(
    () => loadSettings().themePreference
  );
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(getSystemPrefersDark);

  useEffect(() => {
    void loadPolicy().then(orgPolicy => {
      setPolicy(orgPolicy);
      const name = orgPolicy.appName ?? DEFAULT_APP_NAME;
      setAppName(name);
      document.title = name;

      // Deferred: telemetry (ADR-006) is opt-in and non-critical, so init runs
      // after mount/first paint rather than blocking it, protecting the
      // ≤500ms task-pane load target (CLAUDE.md B3). No-ops internally when
      // telemetry is disabled or unconfigured.
      //
      // The OTel SDK itself is code-split behind telemetry.ts's facade, which
      // loads ./telemetry-otel dynamically and only when telemetry is actually
      // enabled — so when it's off (the default) none of that ~52 KiB is
      // fetched. See the header of telemetry.ts.
      void initTelemetry(resolveSettings(orgPolicy));
    });
  }, []);

  // Keep the "system" theme preference in sync with a live OS-level
  // dark/light toggle while the task pane stays open (no reload required).
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia(DARK_MODE_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches);
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  const theme = useMemo(
    () => resolveTheme(themePreference, systemPrefersDark),
    [themePreference, systemPrefersDark]
  );

  return (
    <FluentProvider theme={theme}>
      {showSettings ? (
        <SettingsPanel
          onSave={() => {
            // Settings may have changed the theme preference — re-read it so
            // the new theme takes effect immediately without a full reload.
            setThemePreference(loadSettings().themePreference);
            setShowSettings(false);
          }}
          onClose={() => setShowSettings(false)}
        />
      ) : (
        <ChatPanel appName={appName} onSettings={() => setShowSettings(true)} policy={policy} />
      )}
    </FluentProvider>
  );
}
